import { useState } from 'react';
import useFetch from '../../../hooks/useFetch';
import { ServiceEvents } from '../shared/interfaces';
import { EndPoints } from '../EndPoints';
import { OrderDataForm, OrderFilters } from '../../models/Order';

/**
 * Payload para PUT de facturación del pedido unificado (Order + cotización vinculada).
 * Permite modificar forma de pago, condición de pago y (solo sector Transporte) peso padrón.
 * Moneda y fuente de tipo de cambio quedan bloqueadas y no se incluyen aquí.
 */
export type UpdateOrderInvoicingPayload = {
    payment_method_id?: number | string | null;
    reference?: string | null;
    days_count?: number | string | null;
    payments_count?: number | string | null;
    payment_percentage_1?: number | string | null;
    payment_percentage_2?: number | string | null;
    use_standard_weight?: boolean;
    standard_weight_value?: number | string | null;
    standard_weight_unit_id?: number | string | null;
    standard_weight_lower_limit?: number | string | null;
    standard_weight_lower_limit_unit_id?: number | string | null;
    // Configuración de Tipo de Cambio Prov/Final (moneda y fuente permanecen bloqueadas)
    preliminary_exchange_rate_date_reference?: string | null;
    final_exchange_rate_date_reference?: string | null;
    preliminary_days?: number | string | null;
    preliminary_months?: number | string | null;
    preliminary_year?: number | string | null;
    preliminary_fixed_date?: string | null;
    final_days?: number | string | null;
    final_months?: number | string | null;
    final_year?: number | string | null;
    final_fixed_date?: string | null;
};

const useOrderService = () => {
    const { doPost, doGet, doPut, doDelete } = useFetch();

    const [fetchingStoreOrder, setFetchingStoreOrder] = useState(false);
    const [fetchingGetAllOrders, setFetchingGetAllOrders] = useState(false);
    const [fetchingEditOrder, setFetchingEditOrder] = useState(false);
    const [fetchingUpdateOrder, setFetchingUpdateOrder] = useState(false);
    const [fetchingDeleteOrder, setFetchingDeleteOrder] = useState(false);
    const [fetchingShowOrder, setFetchingShowOrder] = useState(false);
    const [fetchingChangeStatusOrder, setFetchingChangeStatusOrder] = useState(false);
    const [fetchingUpdateOrderInvoicing, setFetchingUpdateOrderInvoicing] = useState(false);

    /**
     * Crea un pedido unificado. El campo `sector` del form determina
     * el flujo de negocio en el backend (TRANSPORTE | ALMACENAMIENTO).
     */
    const storeOrder = (orderForm: OrderDataForm, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: EndPoints.ORDERS.STORE_ORDER,
            body: orderForm,
            setFetching: setFetchingStoreOrder,
        });
    };

    /**
     * Listado unificado de pedidos (ambos sectores).
     */
    const getAllOrders = (filters: OrderFilters, events: ServiceEvents = {}) => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                params.append(key, String(value));
            }
        });
        doGet({
            ...events,
            url: `${EndPoints.ORDERS.GET_ALL_ORDERS}?${params.toString()}`,
            setFetching: setFetchingGetAllOrders,
        });
    };

    const editOrder = (id: number, sector: string, events: ServiceEvents = {}) => {
        const base = sector === 'TRANSPORTE'
            ? EndPoints.TRANSPORTATION_ORDERS.EDIT_TRANSPORTATION_ORDER
            : EndPoints.WAREHOUSE_ORDERS.EDIT_WAREHOUSE_ORDER;
        doGet({
            ...events,
            url: base.replace(':id', id.toString()),
            setFetching: setFetchingEditOrder,
        });
    };

    const editUnifiedOrder = (orderId: number, events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: EndPoints.ORDERS.EDIT_ORDER.replace(':order_id', orderId.toString()),
            setFetching: setFetchingEditOrder,
        });
    };

    const showOrder = (id: number, sector: string, events: ServiceEvents = {}) => {
        const base = sector === 'TRANSPORTE'
            ? EndPoints.TRANSPORTATION_ORDERS.SHOW_TRANSPORTATION_ORDER
            : EndPoints.WAREHOUSE_ORDERS.SHOW_QUOTE_WAREHOUSE_ORDER;
        doGet({
            ...events,
            url: base.replace(':id', id.toString()),
            setFetching: setFetchingShowOrder,
        });
    };

    const showUnifiedOrder = (orderId: number, events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: EndPoints.ORDERS.SHOW_ORDER.replace(':order_id', orderId.toString()),
            setFetching: setFetchingShowOrder,
        });
    };

    const deleteOrder = (id: number, sector: string, events: ServiceEvents = {}) => {
        const base = sector === 'TRANSPORTE'
            ? EndPoints.TRANSPORTATION_ORDERS.DELETE_TRANSPORTATION_ORDER
            : EndPoints.WAREHOUSE_ORDERS.DELETE_WAREHOUSE_ORDER;
        doDelete({
            ...events,
            url: base.replace(':id', id.toString()),
            setFetching: setFetchingDeleteOrder,
        });
    };

    const deleteUnifiedOrder = (orderId: number, events: ServiceEvents = {}) => {
        doDelete({
            ...events,
            url: EndPoints.ORDERS.DELETE_ORDER.replace(':order_id', orderId.toString()),
            setFetching: setFetchingDeleteOrder,
        });
    };

    const changeStatusOrder = (
        id: number,
        sector: string,
        status: string,
        events: ServiceEvents = {}
    ) => {
        const base = sector === 'TRANSPORTE'
            ? EndPoints.TRANSPORTATION_ORDERS.CHANGE_STATUS_TRANSPORTATION_ORDER
            : EndPoints.WAREHOUSE_ORDERS.CHANGE_STATUS_WAREHOUSE_ORDER;
        doPut({
            ...events,
            url: base.replace(':id', id.toString()),
            body: { status },
            setFetching: setFetchingChangeStatusOrder,
        });
    };

    const changeStatusUnifiedOrder = (orderId: number, status: string, events: ServiceEvents = {}) => {
        doPut({
            ...events,
            url: EndPoints.ORDERS.CHANGE_STATUS_ORDER.replace(':order_id', orderId.toString()),
            body: { status },
            setFetching: setFetchingChangeStatusOrder,
        });
    };

    const canChangeStatusUnifiedOrder = (orderId: number, status: string, events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: EndPoints.ORDERS.CAN_CHANGE_STATUS_ORDER
                .replace(':order_id', orderId.toString())
                .replace(':status', status),
            setFetching: setFetchingChangeStatusOrder,
        });
    };

    const updateOrderInvoicing = (orderId: number, data: UpdateOrderInvoicingPayload, events: ServiceEvents = {}) => {
        doPut({
            ...events,
            url: EndPoints.ORDERS.UPDATE_ORDER_INVOICING.replace(':order_id', orderId.toString()),
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: data,
            requestType: 'json',
            setFetching: setFetchingUpdateOrderInvoicing,
        });
    };

    return {
        fetchingStoreOrder,
        fetchingGetAllOrders,
        fetchingEditOrder,
        fetchingUpdateOrder,
        fetchingDeleteOrder,
        fetchingShowOrder,
        fetchingChangeStatusOrder,
        fetchingUpdateOrderInvoicing,
        storeOrder,
        getAllOrders,
        editOrder,
        editUnifiedOrder,
        showOrder,
        showUnifiedOrder,
        deleteOrder,
        deleteUnifiedOrder,
        changeStatusOrder,
        changeStatusUnifiedOrder,
        canChangeStatusUnifiedOrder,
        updateOrderInvoicing,
    };
};

export default useOrderService;
